import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { TbbsReclassService } from './tbbs-reclass.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

type ReclassAccount = {
  template: string;
  templateid?: any;
  systemacct: string;
  systemacctid?: any;
  amount: any;
  type: 'I' | 'E' | 'D';
  trans_header_key: any;
  trans_details_key: any;
  templateError?: boolean;
  systemacctError?: boolean;
  amtError?: boolean;
};

@Component({
    selector: 'gtw-tbbs-reclass',
    templateUrl: './tbbs-reclass.component.html',
    styleUrls: ['./tbbs-reclass.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TbbsReclassComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  modalTitle = 'View-Adjust TBBS';
  showFormLoader = true;
  crudLoading = false;
  existingLoading = false;
  systemaLoading = false;
  templateLoading = false;
  noRecords = false;

  adj_desc = '';
  old_adj_desc = '';
  type = false;
  reclassTotal: number = 0;

  templateError = false;
  systemacctError = false;
  amtError = false;
  desError = false;

  accounts: ReclassAccount[] = [];
  toDeleteTransaction: ReclassAccount[] = [];
  tbbsArray: any[] = [];
  systemAcctMain: any[] = [];
  templateDataMain: any[] = [];
  sys_acc_dropdown_data: string[] = [];
  templatedata: string[] = [];
  showDeleteConfirmDialog = false;
  pendingDeleteRow: any = null;
  showLoadConfirmDialog = false;
  pendingLoadGroup: any = null;
  deleteConfirmConfig: ConfirmationModalConfig = {
    title: 'Delete Reclass',
    message: 'Are you sure you want to permanently delete this reclass?',
    iconClass: 'fa fa-trash',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };
  loadConfirmConfig: ConfirmationModalConfig = {
    title: 'Load Reclass',
    message: 'Are you sure you want to load this reclass? You have unsaved changes',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };

  private isInitialized = false;
  private isLoading = false;
  private initPendingRequests = 0;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: TbbsReclassService,
    private snack: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    this.initPendingRequests = 0;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.showFormLoader = true;
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl || !this.rowData || Object.keys(this.rowData).length === 0) return;
    if (!this.globalService || Object.keys(this.globalService).length === 0) return;
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.accounts = [];
    this.toDeleteTransaction = [];
    this.reclassTotal = 0;
    this.adj_desc = '';
    this.old_adj_desc = '';
    this.templateError = false;
    this.systemacctError = false;
    this.amtError = false;
    this.desError = false;
    this.type = false;
    this.startInitLoading(3);
    this.getSystmAcctDropDown(() => this.completeInitLoading());
    this.getTemplateData(() => this.completeInitLoading());
    this.loadExistingReclass(() => this.completeInitLoading());
    if (this.accounts.length === 0) this.addTargetAccount();
    this.cdr.markForCheck();
  }

  private startInitLoading(requestCount: number): void {
    this.isLoading = true;
    this.showFormLoader = true;
    this.initPendingRequests = requestCount;
    this.cdr.markForCheck();
  }

  private completeInitLoading(): void {
    this.initPendingRequests = Math.max(0, this.initPendingRequests - 1);
    if (this.initPendingRequests === 0) {
      // Ensure first add-section row is always visible once init is complete.
      if (!this.accounts || this.accounts.length === 0) {
        this.addTargetAccount();
      }
      this.isLoading = false;
      this.showFormLoader = false;
      this.cdr.detectChanges();
    } else {
      this.cdr.markForCheck();
    }
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
  }

  private get taxYear(): any {
    return this.globalService?.tax_year || this.filterParams?.tax_year || this.rowData?.TAX_YEAR;
  }

  private get scenario(): any {
    return this.globalService?.scenario || this.filterParams?.scenario || this.rowData?.SCENARIO;
  }

  private get jcdKey(): any {
    return this.globalService?.jcd_key || this.filterParams?.jcd_key || this.rowData?.JCD_KEY;
  }

  private buildLoadPayload(actionCode: string, extra: any): any {
    const merged: any = {
      ...(this.globalService || {}),
      ...(this.filterParams || {}),
      ...(extra || {})
    };
    const chartType = Object.prototype.hasOwnProperty.call(extra || {}, 'chart_type')
      ? extra.chart_type
      : merged.chart_type;

    return {
      ...merged,
      action_code: actionCode,
      activity_key: merged.activity_key ?? this.rowData?.ACTIVITY_KEY ?? '',
      chart_type: chartType,
      filing_key: merged.filing_key ?? this.rowData?.CONSOL_GROUP_KEY ?? '1',
      filter_by: merged.filter_by ?? this.rowData?.ACCT_KEY ?? '',
      is_locked: merged.is_locked ?? 'N',
      issue_id: merged.issue_id ?? '',
      issue_id_for_tx_save: merged.issue_id_for_tx_save ?? '',
      issue_short_desc: merged.issue_short_desc ?? '',
      jcd_key: merged.jcd_key ?? this.rowData?.JCD_KEY ?? '250',
      jcd_ta_key: merged.jcd_ta_key ?? '',
      parent_me_string: merged.parent_me_string ?? '',
      scenario: merged.scenario ?? this.rowData?.SCENARIO ?? '74',
      screen_key: merged.screen_key ?? '',
      screen_type: merged.screen_type ?? 'grid',
      source_system: merged.source_system ?? this.rowData?.SOURCE_SYSTEM ?? this.rowData?.CHART_TYPE ?? '',
      tax_year: merged.tax_year ?? this.rowData?.TAX_YEAR ?? ''
    };
  }

  addTargetAccount(): void {
    const first = this.accounts.length === 0;
    this.accounts.push({
      template: '',
      systemacct: first ? `${this.rowData?.ACCT || ''}-${this.rowData?.ACCT_DESC || ''}` : '',
      amount: '',
      type: 'I',
      trans_header_key: 0,
      trans_details_key: 0
    });
    this.setReclassTotal();
  }

  removeAccount(index: number): void {
    const acct = this.accounts[index];
    if (!acct) return;
    acct.type = 'D';
    this.toDeleteTransaction.push(acct);
    this.accounts.splice(index, 1);
    this.setReclassTotal();
  }

  templateChange(index?: number): void {
    if (index === undefined) {
      this.templateError = false;
      this.accounts.forEach((a) => {
        a.templateError = !a.template;
        if (a.templateError) this.templateError = true;
      });
      return;
    }
    const a = this.accounts[index];
    if (!a) return;
    a.templateError = !a.template;
    this.templateError = !!this.accounts.find((x) => x.templateError);
  }

  acctChange(index?: number): void {
    if (index === undefined) {
      this.systemacctError = false;
      this.accounts.forEach((a) => {
        a.systemacctError = !a.systemacct;
        if (a.systemacctError) this.systemacctError = true;
      });
      return;
    }
    const a = this.accounts[index];
    if (!a) return;
    a.systemacctError = !a.systemacct;
    this.systemacctError = !!this.accounts.find((x) => x.systemacctError);
  }

  amtChange(index?: number): void {
    if (index === undefined) {
      this.amtError = false;
      this.accounts.forEach((a) => {
        const amt = Number(a.amount);
        a.amtError = !a.amount || isNaN(amt) || amt === 0;
        if (a.amtError) this.amtError = true;
      });
    } else {
      const a = this.accounts[index];
      if (!a) return;
      const amt = Number(a.amount);
      a.amtError = !a.amount || isNaN(amt) || amt === 0;
      this.amtError = !!this.accounts.find((x) => x.amtError);
    }
    this.setReclassTotal();
  }

  adjChange(): void {
    this.desError = !this.adj_desc;
  }

  private setReclassTotal(): void {
    let total = 0;
    this.accounts.forEach((a) => {
      const amt = Number(a.amount);
      if (!isNaN(amt)) total += amt;
    });
    this.reclassTotal = total;
  }

  loadReclass(item: any): void {
    this.accounts = [];
    (item?.arrData || []).forEach((d: any) => {
      const templateid = this.templateDataMain.find((t: any) => t.SCHDM_TEMPLATE === d.SCHDM_TEMPLATE)?.SCHDM_TEMPLATE_ID;
      const systemacctid = this.systemAcctMain.find((s: any) => s.SYSTEM_ACCT_DESC === d.SYSTEM_ACCT_DESC)?.SYSTEM_ACCT_KEY;
      this.accounts.push({
        template: d.SCHDM_TEMPLATE,
        templateid,
        systemacct: d.SYSTEM_ACCT_DESC,
        systemacctid,
        amount: d.AMOUNT,
        type: 'E',
        trans_header_key: d.TRANS_HEADER_KEY,
        trans_details_key: d.TRANS_DETAILS_KEY
      });
    });
    this.old_adj_desc = item?.arrData?.[0]?.ADJ_DESCRIPTION || '';
    this.adj_desc = this.old_adj_desc;
    this.type = true;
    this.setReclassTotal();
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.snack.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.templateChange();
    this.adjChange();
    this.acctChange();
    this.amtChange();
    if (this.templateError || this.desError || this.systemacctError || this.amtError || this.reclassTotal !== 0) {
      this.snack.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      return;
    }
    this.crudLoading = true;

    const sendobj: any[] = [];
    const mapCommon = () => ({
      tax_year: this.taxYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      LEID: this.rowData?.LEID,
      CDR: this.rowData?.CDR_NO,
      Reporting_period: this.rowData?.REPORTING_PERIOD,
      me_code: this.rowData?.ME_CODE
    });

    this.toDeleteTransaction.forEach((a) => {
      const systemAcctNew = this.systemAcctMain.find((s: any) => s.SYSTEM_ACCT_DESC === a.systemacct)?.SYSTEM_ACCT_KEY;
      const templateNew = this.templateDataMain.find((t: any) => t.SCHDM_TEMPLATE === a.template)?.SCHDM_TEMPLATE_ID;
      sendobj.push({
        ...mapCommon(),
        adj_desc_old: a.type === 'I' ? this.adj_desc : this.old_adj_desc,
        adj_desc_new: this.adj_desc,
        system_acct_new: systemAcctNew,
        template_new: templateNew,
        system_acct_old: a.type === 'I' ? systemAcctNew : a.systemacctid,
        template_old: a.type === 'I' ? templateNew : a.templateid,
        amount_new: a.amount,
        amount_old: a.amount,
        type: 'D',
        trans_header_key: a.trans_header_key,
        trans_details_key: a.trans_details_key
      });
    });

    this.accounts.forEach((a) => {
      const systemAcctNew = this.systemAcctMain.find((s: any) => s.SYSTEM_ACCT_DESC === a.systemacct)?.SYSTEM_ACCT_KEY;
      const templateNew = this.templateDataMain.find((t: any) => t.SCHDM_TEMPLATE === a.template)?.SCHDM_TEMPLATE_ID;
      sendobj.push({
        ...mapCommon(),
        adj_desc_old: a.type === 'I' ? this.adj_desc : this.old_adj_desc,
        adj_desc_new: this.adj_desc,
        system_acct_new: systemAcctNew,
        template_new: templateNew,
        system_acct_old: a.type === 'I' ? systemAcctNew : a.systemacctid,
        template_old: a.type === 'I' ? templateNew : a.templateid,
        amount_new: a.amount,
        amount_old: a.amount,
        type: a.type === 'I' ? 'I' : 'E',
        trans_header_key: a.trans_header_key,
        trans_details_key: a.trans_details_key
      });
    });

    this.service.saveTBBSReclass(this.baseUrl, sendobj, sendobj, this.globalService || {}, this.filterParams || {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.snack.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.snack.openSnackBar('TBBS Reclass has been successfully Added', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
          this.init();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.crudLoading = false;
        this.snack.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  confirmDeleteReclass(reclassRow: any): void {
    this.pendingDeleteRow = reclassRow;
    this.showDeleteConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onDeleteConfirm(): void {
    if (!this.pendingDeleteRow) {
      this.showDeleteConfirmDialog = false;
      this.cdr.detectChanges();
      return;
    }
    const deleteRow = this.pendingDeleteRow;
    this.pendingDeleteRow = null;
    this.showDeleteConfirmDialog = false;
    this.deleteReclass(deleteRow);
  }

  onDeleteCancel(): void {
    this.pendingDeleteRow = null;
    this.showDeleteConfirmDialog = false;
    this.cdr.detectChanges();
  }

  confirmLoadReclass(group: any): void {
    if (!this.type) {
      this.loadReclass(group);
      return;
    }
    this.pendingLoadGroup = group;
    this.showLoadConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onLoadConfirm(): void {
    const group = this.pendingLoadGroup;
    this.pendingLoadGroup = null;
    this.showLoadConfirmDialog = false;
    if (group) {
      this.loadReclass(group);
    }
    this.cdr.detectChanges();
  }

  onLoadCancel(): void {
    this.pendingLoadGroup = null;
    this.showLoadConfirmDialog = false;
    this.cdr.detectChanges();
  }

  private deleteReclass(reclassRow: any): void {
    this.existingLoading = true;
    const payload = {
      trans_header_key: reclassRow?.TRANS_HEADER_KEY,
      type: 'D',
      trans_details_key: reclassRow?.TRANS_DETAILS_KEY
    };
    this.service.deleteReclass(this.baseUrl, payload, this.globalService || {}, this.filterParams || {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.existingLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.snack.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.snack.openSnackBar('Reclass has been successfully deleted', 'gtw-snackbar--green');
          this.loadExistingReclass();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.existingLoading = false;
        this.snack.openSnackBar('Error while deleting reclass', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.init();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private getSystmAcctDropDown(onDone?: () => void): void {
    this.systemaLoading = true;
    const params = this.buildLoadPayload('31jsr3', {
      chart_type: this.rowData?.CHART_TYPE,
      source_system: this.rowData?.SOURCE_SYSTEM ?? this.rowData?.CHART_TYPE,
      filter_by: this.rowData?.ACCT_KEY
    });
    this.service.loadJson(this.baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.systemAcctMain = data?.jsonObject || [];
        this.sys_acc_dropdown_data = [...new Set(this.systemAcctMain.map((x: any) => x.SYSTEM_ACCT_DESC))];
        this.systemaLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      },
      error: () => {
        this.systemAcctMain = [];
        this.sys_acc_dropdown_data = [];
        this.systemaLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      }
    });
  }

  private getTemplateData(onDone?: () => void): void {
    this.templateLoading = true;
    const params = this.buildLoadPayload('rjubnm', {
      filter_by: this.rowData?.ACCT_KEY,
      source_system: this.rowData?.SOURCE_SYSTEM ?? this.rowData?.CHART_TYPE
    });
    this.service.loadJson(this.baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.templateDataMain = data?.jsonObject || [];
        this.templatedata = [...new Set(this.templateDataMain.map((x: any) => x.SCHDM_TEMPLATE))];
        this.templateLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      },
      error: () => {
        this.templateDataMain = [];
        this.templatedata = [];
        this.templateLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      }
    });
  }

  private loadExistingReclass(onDone?: () => void): void {
    this.existingLoading = true;
    this.tbbsArray = [];
    this.noRecords = false;
    this.service.loadJson(this.baseUrl, {
      action_code: 'iu9fsa',
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      combination_key: this.rowData?.COMBINATION_KEY,
      tax_year: this.taxYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        const reclassArray = data?.jsonObject || [];
        const grouped: Record<string, any[]> = {};
        reclassArray.forEach((row: any) => {
          const key = String(row.TRANS_HEADER_KEY);
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(row);
        });
        this.tbbsArray = Object.keys(grouped).map((k) => ({ trans_key: k, arrData: grouped[k] }));
        this.noRecords = reclassArray.length === 0;
        this.existingLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      },
      error: () => {
        this.tbbsArray = [];
        this.noRecords = true;
        this.existingLoading = false;
        onDone?.();
        this.cdr.detectChanges();
      }
    });
  }
}
